import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Trash2, Music, Clock, Save, Mic, X, Edit2, GripVertical, Lock, AlertCircle, ChevronDown, ChevronRight, Settings, Merge, Split, RotateCcw } from 'lucide-react';
import { useBusiness } from '../context/BusinessContext';
import { useTheme } from '../context/ThemeContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DAYS_OF_WEEK = [
  { key: 'monday', label: 'Monday', shortLabel: 'Mon' },
  { key: 'tuesday', label: 'Tuesday', shortLabel: 'Tue' },
  { key: 'wednesday', label: 'Wednesday', shortLabel: 'Wed' },
  { key: 'thursday', label: 'Thursday', shortLabel: 'Thu' },
  { key: 'friday', label: 'Friday', shortLabel: 'Fri' },
  { key: 'saturday', label: 'Saturday', shortLabel: 'Sat' },
  { key: 'sunday', label: 'Sunday', shortLabel: 'Sun' },
];

// Default slots: 15 one-hour slots from 8 AM to 11 PM
const getDefaultSlots = () => Array.from({ length: 15 }, (_, i) => ({ start_hour: i + 8, end_hour: i + 9 }));

// Predefined color palette for playlists - saturated pastels and vibrant tones
const PLAYLIST_COLORS = [
  // Saturated Pastels (Row 1) - Rich pastels with good visibility
  { name: 'Rose', hex: '#fda4af' },
  { name: 'Melon', hex: '#fdba74' },
  { name: 'Sunflower', hex: '#fef08a' },
  { name: 'Lime Sorbet', hex: '#d9f99d' },
  { name: 'Mint', hex: '#86efac' },
  { name: 'Turquoise', hex: '#5eead4' },
  { name: 'Cerulean', hex: '#7dd3fc' },
  { name: 'Cornflower', hex: '#a5b4fc' },
  { name: 'Violet', hex: '#c4b5fd' },
  { name: 'Mauve', hex: '#f0abfc' },
  // More Saturated Pastels (Row 2) - Deeper pastels
  { name: 'Salmon', hex: '#fca5a5' },
  { name: 'Peach', hex: '#fdba74' },
  { name: 'Butter', hex: '#fcd34d' },
  { name: 'Chartreuse', hex: '#bef264' },
  { name: 'Spring', hex: '#6ee7b7' },
  { name: 'Cyan', hex: '#67e8f9' },
  { name: 'Sky Blue', hex: '#93c5fd' },
  { name: 'Periwinkle', hex: '#a5b4fc' },
  { name: 'Lavender', hex: '#d8b4fe' },
  { name: 'Pink', hex: '#f9a8d4' },
  // Rich Pastels (Row 3) - Bold pastels
  { name: 'Flamingo', hex: '#fb7185' },
  { name: 'Tangerine', hex: '#fb923c' },
  { name: 'Gold', hex: '#facc15' },
  { name: 'Lime', hex: '#a3e635' },
  { name: 'Jade', hex: '#4ade80' },
  { name: 'Aquamarine', hex: '#2dd4bf' },
  { name: 'Azure', hex: '#38bdf8' },
  { name: 'Iris', hex: '#818cf8' },
  { name: 'Orchid', hex: '#c084fc' },
  { name: 'Fuchsia', hex: '#e879f9' },
  // Vibrant colors (Row 4) - Full saturation
  { name: 'Coral', hex: '#e94560' },
  { name: 'Hot Pink', hex: '#f43f5e' },
  { name: 'Magenta', hex: '#ec4899' },
  { name: 'Purple', hex: '#d946ef' },
  { name: 'Grape', hex: '#a855f7' },
  { name: 'Indigo', hex: '#6366f1' },
  { name: 'Blue', hex: '#3b82f6' },
  { name: 'Ocean', hex: '#0ea5e9' },
  { name: 'Teal', hex: '#14b8a6' },
  { name: 'Emerald', hex: '#10b981' },
  // More vibrant & earth tones (Row 5)
  { name: 'Green', hex: '#22c55e' },
  { name: 'Lime Green', hex: '#84cc16' },
  { name: 'Yellow', hex: '#eab308' },
  { name: 'Amber', hex: '#f59e0b' },
  { name: 'Orange', hex: '#f97316' },
  { name: 'Red', hex: '#ef4444' },
  { name: 'Sage', hex: '#8b9a7d' },
  { name: 'Bronze', hex: '#c4a35a' },
  { name: 'Tan', hex: '#d4a574' },
  { name: 'Rust', hex: '#b4654a' },
  // Deep & Muted tones (Row 6)
  { name: 'Burgundy', hex: '#be123c' },
  { name: 'Plum', hex: '#a21caf' },
  { name: 'Royal', hex: '#7c3aed' },
  { name: 'Navy', hex: '#4338ca' },
  { name: 'Cobalt', hex: '#2563eb' },
  { name: 'Peacock', hex: '#0891b2' },
  { name: 'Forest', hex: '#059669' },
  { name: 'Olive', hex: '#65a30d' },
  { name: 'Mustard', hex: '#ca8a04' },
  { name: 'Terracotta', hex: '#c2410c' },
];

const PlaylistScheduler = () => {
  const { selectedBusinessId, isSuperAdmin, businesses } = useBusiness();
  const { isLight } = useTheme();
  const [playlists, setPlaylists] = useState([]);
  const [songs, setSongs] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [genres, setGenres] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editingPlaylist, setEditingPlaylist] = useState(null);
  const [newPlaylist, setNewPlaylist] = useState({
    name: '',
    items: [],
    assigned_businesses: [],
    color: null,
    font_color: null
  });
  const [draggedPlaylist, setDraggedPlaylist] = useState(null);
  const [hoveredCell, setHoveredCell] = useState(null); // { day, slotIndex }
  const [currentTime, setCurrentTime] = useState(new Date());
  
  // Slot configuration state
  const [slotConfig, setSlotConfig] = useState(getDefaultSlots());
  const [slotManagerOpen, setSlotManagerOpen] = useState(false);
  const [tempSlotConfig, setTempSlotConfig] = useState([]);
  
  const hours = Array.from({ length: 15 }, (_, i) => i + 8); // 8 AM to 10 PM (for reference)

  // Get current day of week
  const getCurrentDayKey = () => {
    const dayIndex = currentTime.getDay();
    const dayMap = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    return dayMap[dayIndex];
  };

  // Update current time every second to track active playlists
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // Generate consistent colors for playlists
  // Japandi-inspired solid playlist colors (warm, muted, natural tones)
  const playlistColors = [
    { bg: '#e94560', border: '#d63d56', light: '#fff1f3', text: '#ffffff', lightText: '#e94560' }, // Coral (logo)
    { bg: '#c4a35a', border: '#a68b4b', light: '#faf6ed', text: '#ffffff', lightText: '#8b7355' }, // Warm gold/ochre
    { bg: '#8b9a7d', border: '#7a8a6c', light: '#f5f7f3', text: '#ffffff', lightText: '#6b7a5d' }, // Sage green
    { bg: '#b5838d', border: '#a5737d', light: '#faf5f6', text: '#ffffff', lightText: '#95636d' }, // Dusty rose
    { bg: '#6b8e8e', border: '#5b7e7e', light: '#f3f7f7', text: '#ffffff', lightText: '#4b6e6e' }, // Teal slate
    { bg: '#c49a6c', border: '#b48a5c', light: '#faf7f3', text: '#ffffff', lightText: '#a47a4c' }, // Warm terracotta
    { bg: '#7d8597', border: '#6d7587', light: '#f5f6f8', text: '#ffffff', lightText: '#5d6577' }, // Blue grey
    { bg: '#9b8b7d', border: '#8b7b6d', light: '#f8f6f5', text: '#ffffff', lightText: '#7b6b5d' }, // Warm stone
  ];

  const getPlaylistColor = (playlistId) => {
    // First check if the playlist has a custom color set
    const playlist = playlists.find(p => p.id === playlistId);
    if (playlist && playlist.color) {
      // Return SOLID color from the playlist
      const colorHex = playlist.color;
      return {
        bg: colorHex,           // Solid background color
        light: colorHex,        // Same solid color for light mode
        border: colorHex,       // Border same as bg
        text: '#ffffff',        // White text for contrast
        lightText: colorHex
      };
    }
    // Fall back to the default palette (solid colors)
    const index = playlists.findIndex(p => p.id === playlistId);
    return playlistColors[index % playlistColors.length];
  };

  // Check if a playlist is currently being played
  const isPlaylistCurrentlyPlaying = (playlist) => {
    if (!playlist.time_slots || playlist.time_slots.length === 0) return false;
    
    const now = currentTime;
    const currentSeconds = now.getHours() * 3600 + now.getMinutes() * 60 + now.getSeconds();
    const currentDayKey = getCurrentDayKey();
    
    return playlist.time_slots.some(slot => {
      const slotDay = slot.day_of_week || 'monday'; // Default for legacy data
      if (slotDay !== currentDayKey) return false;
      
      const [startHour, startMin] = slot.start_time.split(':').map(Number);
      const [endHour, endMin] = slot.end_time.split(':').map(Number);
      const startSeconds = startHour * 3600 + startMin * 60;
      const endSeconds = endHour * 3600 + endMin * 60;
      return currentSeconds >= startSeconds && currentSeconds < endSeconds;
    });
  };

  // Calculate total duration of playlist items
  const calculatePlaylistDuration = (items) => {
    return items.reduce((total, item) => {
      if (item.type === 'song') {
        const song = songs.find(s => s.id === item.id);
        return total + (song?.duration || 180);
      } else {
        const announcement = announcements.find(a => a.id === item.id);
        return total + (announcement?.duration || 30);
      }
    }, 0);
  };

  // Format duration to readable string
  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    if (mins >= 60) {
      const hours = Math.floor(mins / 60);
      const remainMins = mins % 60;
      return `${hours}h ${remainMins}m`;
    }
    return `${mins}m ${secs}s`;
  };

  // Calculate item times for a scheduled playlist
  const calculateItemTimes = (playlist, slot) => {
    if (!playlist.items || playlist.items.length === 0) return [];
    
    const [startHour, startMin] = slot.start_time.split(':').map(Number);
    const [endHour, endMin] = slot.end_time.split(':').map(Number);
    const slotStartSeconds = startHour * 3600 + startMin * 60;
    const slotEndSeconds = endHour * 3600 + endMin * 60;
    const slotDuration = slotEndSeconds - slotStartSeconds;
    
    const items = [];
    let currentOffset = 0;
    let loopCount = 0;
    
    while (currentOffset < slotDuration) {
      for (const item of playlist.items) {
        if (currentOffset >= slotDuration) break;
        
        let duration = 180;
        let genre = item.genre || null;
        
        if (item.type === 'song') {
          const song = songs.find(s => s.id === item.id);
          duration = item.duration || song?.duration || 180;
          genre = item.genre || song?.genre || null;
        } else {
          const announcement = announcements.find(a => a.id === item.id);
          duration = item.duration || announcement?.duration || 30;
        }
        
        const itemStart = slotStartSeconds + currentOffset;
        const itemEnd = Math.min(itemStart + duration, slotEndSeconds);
        
        items.push({
          ...item,
          name: item.name,
          genre: genre,
          startTime: formatSecondsToTime(itemStart),
          endTime: formatSecondsToTime(itemEnd),
          duration: formatDuration(duration),
          loopNumber: loopCount + 1
        });
        
        currentOffset += duration;
      }
      loopCount++;
    }
    
    return items;
  };

  const formatSecondsToTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    const period = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours > 12 ? hours - 12 : hours === 0 ? 12 : hours;
    return `${displayHours}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')} ${period}`;
  };

  useEffect(() => {
    fetchData();
    fetchGenres();
    fetchSlotConfig();
  }, [selectedBusinessId]);

  const fetchSlotConfig = async () => {
    if (!selectedBusinessId) {
      setSlotConfig(getDefaultSlots());
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/slot-config?business_id=${selectedBusinessId}`, { headers });
      setSlotConfig(response.data.slots || getDefaultSlots());
    } catch (error) {
      console.error('Failed to fetch slot config:', error);
      setSlotConfig(getDefaultSlots());
    }
  };

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const businessQuery = selectedBusinessId ? `?business_id=${selectedBusinessId}` : '';

      const [playlistsRes, songsRes, announcementsRes] = await Promise.all([
        axios.get(`${API}/playlists${businessQuery}`, { headers }),
        axios.get(`${API}/songs`, { headers }),
        axios.get(`${API}/announcements${businessQuery}`, { headers })
      ]);

      setPlaylists(playlistsRes.data);
      setSongs(songsRes.data);
      setAnnouncements(announcementsRes.data);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const fetchGenres = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/genres`, { headers });
      setGenres(response.data);
    } catch (error) {
      console.error('Failed to fetch genres:', error);
    }
  };

  // Slot management functions
  const openSlotManager = () => {
    setTempSlotConfig([...slotConfig]);
    setSlotManagerOpen(true);
  };

  const mergeSlotWithNext = (index) => {
    if (index >= tempSlotConfig.length - 1) return;
    
    const newConfig = [...tempSlotConfig];
    const currentSlot = newConfig[index];
    const nextSlot = newConfig[index + 1];
    
    // Merge: extend current slot's end to next slot's end
    newConfig[index] = {
      start_hour: currentSlot.start_hour,
      end_hour: nextSlot.end_hour
    };
    
    // Remove the next slot
    newConfig.splice(index + 1, 1);
    
    setTempSlotConfig(newConfig);
  };

  const splitSlot = (index) => {
    const slot = tempSlotConfig[index];
    const duration = slot.end_hour - slot.start_hour;
    
    if (duration < 2) {
      toast.error('Cannot split a 1-hour slot');
      return;
    }
    
    const newConfig = [...tempSlotConfig];
    const midPoint = slot.start_hour + Math.floor(duration / 2);
    
    // Replace current slot with two slots
    newConfig.splice(index, 1, 
      { start_hour: slot.start_hour, end_hour: midPoint },
      { start_hour: midPoint, end_hour: slot.end_hour }
    );
    
    setTempSlotConfig(newConfig);
  };

  const saveSlotConfig = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      await axios.put(`${API}/slot-config?business_id=${selectedBusinessId}`, {
        slots: tempSlotConfig
      }, { headers });
      
      // Update local state immediately
      setSlotConfig([...tempSlotConfig]);
      setSlotManagerOpen(false);
      toast.success('Time slot configuration saved!');
      
      // Also refresh from server to ensure consistency
      await fetchSlotConfig();
      await fetchData();
    } catch (error) {
      const msg = error.response?.data?.detail || 'Failed to save slot configuration';
      toast.error(msg);
    }
  };

  const resetSlotConfig = async () => {
    if (!window.confirm('Reset to default 1-hour slots? This will affect the schedule grid and recalculate song durations for existing schedules.')) return;
    
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      // Reset slot config on server
      await axios.post(`${API}/slot-config/reset?business_id=${selectedBusinessId}`, {}, { headers });
      
      // Update playlists that have multi-hour time slots - split them into 1-hour slots
      const defaultSlots = getDefaultSlots();
      const playlistsToUpdate = [];
      
      for (const playlist of playlists) {
        if (!playlist.time_slots || playlist.time_slots.length === 0) continue;
        
        let hasMultiHourSlots = false;
        const newTimeSlots = [];
        
        for (const slot of playlist.time_slots) {
          const startHour = parseInt(slot.start_time.split(':')[0]);
          const endHour = parseInt(slot.end_time.split(':')[0]);
          const duration = endHour - startHour;
          
          if (duration > 1) {
            hasMultiHourSlots = true;
            // Split the multi-hour slot into individual 1-hour slots
            for (let h = startHour; h < endHour; h++) {
              newTimeSlots.push({
                day_of_week: slot.day_of_week,
                start_time: `${h.toString().padStart(2, '0')}:00`,
                end_time: `${(h + 1).toString().padStart(2, '0')}:00`,
                song_ids: slot.song_ids || []
              });
            }
          } else {
            newTimeSlots.push(slot);
          }
        }
        
        if (hasMultiHourSlots) {
          playlistsToUpdate.push({ ...playlist, time_slots: newTimeSlots });
        }
      }
      
      // Update all affected playlists
      for (const playlist of playlistsToUpdate) {
        await axios.put(`${API}/playlists/${playlist.id}`, {
          name: playlist.name,
          items: playlist.items || [],
          time_slots: playlist.time_slots
        }, { headers });
      }
      
      setSlotConfig(defaultSlots);
      setTempSlotConfig(defaultSlots);
      
      if (playlistsToUpdate.length > 0) {
        toast.success(`Slot configuration reset. ${playlistsToUpdate.length} playlist(s) updated with recalculated durations.`);
      } else {
        toast.success('Slot configuration reset to default');
      }
      
      // Refresh data to get updated playlists
      fetchData();
    } catch (error) {
      console.error('Reset slot config error:', error);
      toast.error('Failed to reset slot configuration');
    }
  };

  const formatHour = (hour) => {
    if (hour === 12) return '12PM';
    if (hour > 12) return `${hour - 12}PM`;
    return `${hour}AM`;
  };

  const handleAddItem = (item, type) => {
    const targetPlaylist = editOpen ? editingPlaylist : newPlaylist;
    const updatedItems = [...targetPlaylist.items, { ...item, type, itemId: `${type}-${item.id}-${Date.now()}` }];
    
    if (editOpen) {
      setEditingPlaylist({ ...editingPlaylist, items: updatedItems });
    } else {
      setNewPlaylist({ ...newPlaylist, items: updatedItems });
    }
  };

  const handleRemoveItem = (itemId) => {
    if (editOpen) {
      setEditingPlaylist({
        ...editingPlaylist,
        items: editingPlaylist.items.filter(item => item.itemId !== itemId)
      });
    } else {
      setNewPlaylist({
        ...newPlaylist,
        items: newPlaylist.items.filter(item => item.itemId !== itemId)
      });
    }
  };

  const handleMoveItemUp = (index) => {
    if (index === 0) return;
    const targetPlaylist = editOpen ? editingPlaylist : newPlaylist;
    const newItems = [...targetPlaylist.items];
    [newItems[index - 1], newItems[index]] = [newItems[index], newItems[index - 1]];
    
    if (editOpen) {
      setEditingPlaylist({ ...editingPlaylist, items: newItems });
    } else {
      setNewPlaylist({ ...newPlaylist, items: newItems });
    }
  };

  const handleMoveItemDown = (index) => {
    const targetPlaylist = editOpen ? editingPlaylist : newPlaylist;
    if (index === targetPlaylist.items.length - 1) return;
    const newItems = [...targetPlaylist.items];
    [newItems[index], newItems[index + 1]] = [newItems[index + 1], newItems[index]];
    
    if (editOpen) {
      setEditingPlaylist({ ...editingPlaylist, items: newItems });
    } else {
      setNewPlaylist({ ...newPlaylist, items: newItems });
    }
  };

  const handleCreatePlaylist = async () => {
    if (!newPlaylist.name.trim()) {
      toast.error('Please enter playlist name');
      return;
    }
    if (newPlaylist.items.length === 0) {
      toast.error('Please add at least one song or announcement');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const businessQuery = selectedBusinessId ? `?business_id=${selectedBusinessId}` : '';
      
      const payload = {
        name: newPlaylist.name,
        time_slots: [],
        items: newPlaylist.items,
        color: newPlaylist.color,
        font_color: newPlaylist.font_color
      };
      
      // If super admin and assigning to businesses
      if (isSuperAdmin && newPlaylist.assigned_businesses && newPlaylist.assigned_businesses.length > 0) {
        payload.assign_to_businesses = newPlaylist.assigned_businesses;
      }
      
      await axios.post(`${API}/playlists${businessQuery}`, payload, { 
        headers: { Authorization: `Bearer ${token}` } 
      });

      const assignCount = newPlaylist.assigned_businesses?.length || 0;
      if (assignCount > 0) {
        toast.success(`Playlist created and copied to ${assignCount} business(es)!`);
      } else {
        toast.success('Playlist created!');
      }
      setCreateOpen(false);
      setNewPlaylist({ name: '', items: [], assigned_businesses: [], color: null, font_color: null });
      fetchData();
    } catch (error) {
      toast.error('Failed to create playlist');
    }
  };

  const handleUpdatePlaylist = async () => {
    if (!editingPlaylist.name.trim()) {
      toast.error('Please enter playlist name');
      return;
    }
    if (editingPlaylist.items.length === 0) {
      toast.error('Please add at least one song or announcement');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      
      await axios.put(`${API}/playlists/${editingPlaylist.id}`, {
        name: editingPlaylist.name,
        items: editingPlaylist.items,
        time_slots: editingPlaylist.time_slots || [],
        color: editingPlaylist.color,
        font_color: editingPlaylist.font_color
      }, { headers: { Authorization: `Bearer ${token}` } });

      toast.success('Playlist updated! Times will auto-recalculate.');
      setEditOpen(false);
      setEditingPlaylist(null);
      fetchData();
    } catch (error) {
      console.error('Update error:', error);
      toast.error('Failed to update playlist');
    }
  };

  const handleEditPlaylist = (playlist) => {
    // Super admin can edit even when playlist is playing
    if (!isSuperAdmin && isPlaylistCurrentlyPlaying(playlist)) {
      toast.error('Cannot edit playlist while it is currently playing');
      return;
    }
    
    if (isSuperAdmin && isPlaylistCurrentlyPlaying(playlist)) {
      toast.warning('Editing a currently playing playlist - changes will apply immediately');
    }
    
    setEditingPlaylist({
      ...playlist,
      items: playlist.items || []
    });
    setEditOpen(true);
  };

  const handleSchedulePlaylist = async (playlist, dayKey, hour, endHour) => {
    // Check for conflicts on the same day
    const hasConflict = playlists.some(p => 
      p.time_slots && p.time_slots.some(slot => {
        const slotDay = slot.day_of_week || 'monday';
        if (slotDay !== dayKey) return false;
        
        const startHour = parseInt(slot.start_time.split(':')[0]);
        const slotEndHour = parseInt(slot.end_time.split(':')[0]);
        return (hour >= startHour && hour < slotEndHour) || 
               (endHour > startHour && endHour <= slotEndHour) ||
               (hour <= startHour && endHour >= slotEndHour);
      })
    );

    if (hasConflict) {
      toast.error('This time range overlaps with an existing schedule');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const startTime = `${hour.toString().padStart(2, '0')}:00`;
      const endTime = `${endHour.toString().padStart(2, '0')}:00`;

      const newSlot = {
        day_of_week: dayKey,
        start_time: startTime,
        end_time: endTime,
        song_ids: playlist.items?.map(item => item.id) || []
      };

      const updatedSlots = [...(playlist.time_slots || []), newSlot];

      await axios.put(`${API}/playlists/${playlist.id}`, {
        name: playlist.name,
        items: playlist.items || [],
        time_slots: updatedSlots
      }, { headers: { Authorization: `Bearer ${token}` } });

      const dayLabel = DAYS_OF_WEEK.find(d => d.key === dayKey)?.label || dayKey;
      toast.success(`Scheduled for ${dayLabel} ${startTime} to ${endTime}!`);
      fetchData();
    } catch (error) {
      console.error('Schedule error:', error);
      toast.error('Failed to schedule playlist');
    }
  };

  const handleDragStart = (playlist) => {
    // Allow dragging even if playing - user can schedule additional slots
    setDraggedPlaylist(playlist);
  };

  const handleDragOver = (e, dayKey, slotIndex) => {
    e.preventDefault();
    setHoveredCell({ day: dayKey, slotIndex });
  };

  const handleDragLeave = () => {
    setHoveredCell(null);
  };

  const handleDrop = (e, dayKey, slotIndex) => {
    e.preventDefault();
    setHoveredCell(null);
    
    if (!draggedPlaylist) return;

    const slot = slotConfig[slotIndex];
    if (!slot) return;

    const existingPlaylist = getPlaylistForSlot(dayKey, slotIndex);
    if (existingPlaylist) {
      toast.error('This time slot is already occupied');
      setDraggedPlaylist(null);
      return;
    }

    const startHour = slot.start_hour;
    const endHour = slot.end_hour;
    
    if (endHour > 23) {
      toast.error('Cannot schedule beyond 11 PM');
      setDraggedPlaylist(null);
      return;
    }

    handleSchedulePlaylist(draggedPlaylist, dayKey, startHour, endHour);
    setDraggedPlaylist(null);
  };

  const handleDeleteSchedule = async (playlist, slotIndex) => {
    const slot = playlist.time_slots[slotIndex];
    const now = currentTime;
    const currentSeconds = now.getHours() * 3600 + now.getMinutes() * 60 + now.getSeconds();
    const currentDayKey = getCurrentDayKey();
    
    const slotDay = slot.day_of_week || 'monday';
    const [startHour, startMin] = slot.start_time.split(':').map(Number);
    const [endHour, endMin] = slot.end_time.split(':').map(Number);
    const startSeconds = startHour * 3600 + startMin * 60;
    const endSeconds = endHour * 3600 + endMin * 60;
    
    const isCurrentlyPlaying = slotDay === currentDayKey && currentSeconds >= startSeconds && currentSeconds < endSeconds;
    
    // Super admin can remove even currently playing schedules
    if (isCurrentlyPlaying && !isSuperAdmin) {
      toast.error('Cannot remove schedule while playlist is currently playing');
      return;
    }
    
    // Show special confirmation for super admin removing playing schedule
    if (isCurrentlyPlaying && isSuperAdmin) {
      if (!window.confirm('This schedule is currently playing. Removing it will stop playback. Continue?')) {
        return;
      }
    } else {
      if (!window.confirm('Remove this schedule?')) return;
    }

    try {
      const token = localStorage.getItem('token');
      const updatedSlots = playlist.time_slots.filter((_, i) => i !== slotIndex);
      
      await axios.put(`${API}/playlists/${playlist.id}`, {
        name: playlist.name,
        items: playlist.items || [],
        time_slots: updatedSlots
      }, { headers: { Authorization: `Bearer ${token}` } });

      toast.success('Schedule removed');
      fetchData();
    } catch (error) {
      console.error('Delete schedule error:', error);
      toast.error('Failed to remove schedule');
    }
  };

  const handleDeletePlaylist = async (playlistId) => {
    const playlist = playlists.find(p => p.id === playlistId);
    
    // Super admin can delete even when playlist is playing
    if (!isSuperAdmin && isPlaylistCurrentlyPlaying(playlist)) {
      toast.error('Cannot delete playlist while it is currently playing');
      return;
    }
    
    const confirmMsg = isSuperAdmin && isPlaylistCurrentlyPlaying(playlist)
      ? 'This playlist is currently playing. Deleting it will stop playback. Continue?'
      : 'Delete this playlist?';
    
    if (!window.confirm(confirmMsg)) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/playlists/${playlistId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Playlist deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete playlist');
    }
  };

  // Get playlist scheduled for a specific slot (using slot index)
  const getPlaylistForSlot = (dayKey, slotIndex) => {
    const slot = slotConfig[slotIndex];
    if (!slot) return null;
    
    return playlists.find(playlist => 
      playlist.time_slots && playlist.time_slots.some(ts => {
        const tsDay = ts.day_of_week || 'monday';
        if (tsDay !== dayKey) return false;
        
        const tsStartHour = parseInt(ts.start_time.split(':')[0]);
        const tsEndHour = parseInt(ts.end_time.split(':')[0]);
        
        // Check if this playlist's time slot overlaps with our config slot
        return tsStartHour < slot.end_hour && tsEndHour > slot.start_hour;
      })
    );
  };

  // Get playlist scheduled for a specific day and hour (legacy support for Today's Schedule)
  const getPlaylistForCell = (dayKey, hour) => {
    return playlists.find(playlist => 
      playlist.time_slots && playlist.time_slots.some(slot => {
        const slotDay = slot.day_of_week || 'monday';
        if (slotDay !== dayKey) return false;
        
        const startHour = parseInt(slot.start_time.split(':')[0]);
        const endHour = parseInt(slot.end_time.split(':')[0]);
        return hour >= startHour && hour < endHour;
      })
    );
  };

  // Get the slot from slotConfig that contains a specific hour
  const getSlotForHour = (hour) => {
    return slotConfig.findIndex(slot => hour >= slot.start_hour && hour < slot.end_hour);
  };

  const getPlaylistCounts = (playlist) => {
    const items = playlist.items || [];
    const songCount = items.filter(item => item.type === 'song').length;
    const announcementCount = items.filter(item => item.type === 'announcement').length;
    return { songCount, announcementCount };
  };

  const isSlotCurrentlyPlaying = (dayKey, slotIndex) => {
    const currentHour = currentTime.getHours();
    const currentDayKey = getCurrentDayKey();
    const slot = slotConfig[slotIndex];
    
    if (!slot || dayKey !== currentDayKey) return false;
    return currentHour >= slot.start_hour && currentHour < slot.end_hour;
  };

  const isCellCurrentlyPlaying = (dayKey, hour) => {
    const currentHour = currentTime.getHours();
    const currentDayKey = getCurrentDayKey();
    return dayKey === currentDayKey && hour === currentHour;
  };

  // Check if user can modify slots (admin or super admin)
  // Super admin can always manage, business admin can manage their own business slots
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isBusinessAdmin = user?.role === 'admin';
  const canManageSlots = isSuperAdmin || isBusinessAdmin;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className={isLight ? 'text-gray-500' : 'text-slate-400'}>Loading...</div>
      </div>
    );
  }

  const currentPlaylist = editOpen ? editingPlaylist : newPlaylist;
  const currentDayKey = getCurrentDayKey();

  return (
    <div className={`p-8 min-h-screen ${isLight ? 'bg-[#faf8f5]' : ''}`} data-testid="playlist-scheduler">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className={`text-3xl font-bold mb-2 ${isLight ? 'text-gray-800' : 'text-white'}`}>
              Weekly Playlist Scheduler
            </h1>
            <p className={isLight ? 'text-gray-500' : 'text-slate-400'}>Create playlists and drag them to schedule by day • Times auto-calculate</p>
          </div>

          <div className="flex items-center gap-4">
            <div className={`text-sm ${isLight ? 'text-gray-500' : 'text-slate-400'}`}>
              <span className={`font-medium ${isLight ? 'text-[#e94560]' : 'text-violet-400'}`}>{DAYS_OF_WEEK.find(d => d.key === currentDayKey)?.label}</span>
              {' '}at{' '}
              <span className={`font-mono ${isLight ? 'text-[#e94560]' : 'text-violet-400'}`}>{currentTime.toLocaleTimeString()}</span>
            </div>
            <Dialog open={createOpen} onOpenChange={setCreateOpen}>
              <DialogTrigger asChild>
                <Button className={`rounded-full px-6 ${isLight ? 'bg-[#e94560] hover:bg-[#d63d56]' : 'bg-violet-600 hover:bg-violet-500'} text-white`} data-testid="create-playlist-button">
                  <Plus className="w-4 h-4 mr-2" />
                  Create Playlist
                </Button>
              </DialogTrigger>
              <DialogContent className={`max-w-5xl w-[90vw] max-h-[85vh] overflow-hidden ${isLight ? 'bg-white border-[#ebe7e0] text-gray-800' : 'bg-slate-900 border-slate-800 text-white'}`}>
                <DialogHeader>
                  <DialogTitle className={isLight ? 'text-gray-800' : ''}>Create New Playlist</DialogTitle>
                </DialogHeader>
                <PlaylistForm
                  playlist={newPlaylist}
                  setPlaylist={setNewPlaylist}
                  songs={songs}
                  announcements={announcements}
                  genres={genres}
                  onAddItem={handleAddItem}
                  onRemoveItem={handleRemoveItem}
                  onMoveUp={handleMoveItemUp}
                  onMoveDown={handleMoveItemDown}
                  onSave={handleCreatePlaylist}
                  onCancel={() => setCreateOpen(false)}
                  calculateDuration={calculatePlaylistDuration}
                  formatDuration={formatDuration}
                  isSuperAdmin={isSuperAdmin}
                  businesses={businesses}
                  isLight={isLight}
                />
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Playlists List */}
        {playlists.length > 0 && (
          <div className={`rounded-xl p-6 ${isLight ? 'bg-white border border-[#ebe7e0]' : 'bg-slate-900/40 border border-slate-800'}`}>
            <h3 className={`text-lg font-semibold mb-4 ${isLight ? 'text-gray-800' : 'text-white'}`}>Your Playlists (Drag to schedule)</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {playlists.map((playlist) => {
                const { songCount, announcementCount } = getPlaylistCounts(playlist);
                const color = getPlaylistColor(playlist.id);
                const isPlaying = isPlaylistCurrentlyPlaying(playlist);
                const totalDuration = calculatePlaylistDuration(playlist.items || []);
                // Super admin can always interact with playlists
                const isLocked = isPlaying && !isSuperAdmin;
                
                // Determine if the playlist has a custom color for solid background
                const hasCustomColor = playlist.color != null;
                
                return (
                  <div
                    key={playlist.id}
                    draggable={true}
                    onDragStart={() => handleDragStart(playlist)}
                    className="cursor-move hover:shadow-lg border-2 rounded-lg p-4 transition-colors relative"
                    style={{
                      backgroundColor: hasCustomColor ? color.bg : color.light,
                      borderColor: hasCustomColor ? color.border : color.border
                    }}
                    data-testid={`playlist-card-${playlist.id}`}
                  >
                    {/* Playing indicator - red blinking dot */}
                    {isPlaying && (
                      <div className="absolute top-2 right-2">
                        <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse shadow-lg shadow-red-500/50" />
                      </div>
                    )}
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-2 flex-1">
                        <GripVertical className="w-4 h-4 text-white/70" />
                        {isPlaying && <Lock className="w-4 h-4 text-white" />}
                        <h4 className="font-medium text-white">
                          {playlist.name}
                        </h4>
                        {(playlist.from_template_id || playlist.created_by_super_admin) && (
                          <span className="px-1.5 py-0.5 text-[10px] rounded bg-white/20 border border-white/30 text-white">
                            From Template
                          </span>
                        )}
                      </div>
                      <div className="flex gap-1 mr-4">
                        <Button
                          onClick={() => handleEditPlaylist(playlist)}
                          variant="ghost"
                          disabled={isLocked}
                          className={`h-6 w-6 p-0 ${isLocked ? 'text-white/30 cursor-not-allowed' : isPlaying && isSuperAdmin ? 'text-amber-400 hover:text-amber-300' : 'text-white/80 hover:text-white'}`}
                          title={isLocked ? "Cannot edit while playing" : isPlaying && isSuperAdmin ? "Edit (Super Admin override)" : "Edit playlist"}
                          data-testid={`edit-playlist-${playlist.id}`}
                        >
                          <Edit2 className="w-3 h-3" />
                        </Button>
                        <Button
                          onClick={() => handleDeletePlaylist(playlist.id)}
                          variant="ghost"
                          disabled={isLocked}
                          className={`h-6 w-6 p-0 ${isLocked ? 'text-white/30 cursor-not-allowed' : isPlaying && isSuperAdmin ? 'text-amber-400 hover:text-amber-300' : 'text-white/80 hover:text-white hover:text-red-400'}`}
                          title={isLocked ? "Cannot delete while playing" : isPlaying && isSuperAdmin ? "Delete (Super Admin override)" : "Delete playlist"}
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                    {isPlaying && (
                      <div className="flex items-center gap-1 mb-2 text-xs text-white/90">
                        <div className="w-1.5 h-1.5 rounded-full animate-pulse bg-white" />
                        Now Playing{isSuperAdmin ? ' - Super Admin can modify' : ' - Drag to add more slots'}
                      </div>
                    )}
                    <div className="flex items-center gap-3 text-xs text-white/80">
                      <div className="flex items-center gap-1">
                        <Music className="w-3 h-3 text-white/90" />
                        <span>{songCount} song{songCount !== 1 ? 's' : ''}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Mic className="w-3 h-3 text-white/90" />
                        <span>{announcementCount} ann.</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Clock className="w-3 h-3 text-white/70" />
                        <span>{formatDuration(totalDuration)}</span>
                      </div>
                    </div>
                    <div className="text-xs mt-1 text-white/70">
                      {playlist.time_slots?.length || 0} schedule(s)
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Weekly Schedule Grid */}
        <div className={`rounded-xl p-6 overflow-x-auto ${isLight ? 'bg-white border border-[#ebe7e0]' : 'bg-slate-900/40 border border-slate-800'}`}>
          <div className="flex items-center justify-between mb-4">
            <h3 className={`text-lg font-semibold ${isLight ? 'text-gray-800' : 'text-white'}`}>Weekly Schedule (Auto-loops to fill time slot)</h3>
            {canManageSlots && selectedBusinessId && (
              <Button
                onClick={openSlotManager}
                variant="outline"
                className={isLight ? 'border-[#ebe7e0] text-gray-600 hover:bg-gray-50' : 'border-slate-700 text-slate-300 hover:bg-slate-800'}
                data-testid="manage-time-slots-btn"
              >
                <Settings className="w-4 h-4 mr-2" />
                Manage Time Slots
              </Button>
            )}
          </div>
          
          <div className="w-full">
            {/* Time Header - Dynamic width based on slot duration */}
            <div className="flex gap-0.5 mb-2">
              <div className="w-16 flex-shrink-0"></div>
              {slotConfig.map((slot, idx) => {
                const currentHour = currentTime.getHours();
                const isCurrentSlot = currentHour >= slot.start_hour && currentHour < slot.end_hour;
                const slotDuration = slot.end_hour - slot.start_hour;
                
                return (
                  <div 
                    key={idx} 
                    className={`text-center font-medium ${
                      isCurrentSlot ? 'text-emerald-400' : (isLight ? 'text-gray-600' : 'text-slate-300')
                    }`}
                    style={{ flex: slotDuration, minWidth: `${slotDuration * 50}px`, fontSize: '11px' }}
                  >
                    {formatHour(slot.start_hour)}-{formatHour(slot.end_hour)}
                  </div>
                );
              })}
            </div>

            {/* Day Rows */}
            {DAYS_OF_WEEK.map((day) => {
              const isCurrentDay = day.key === currentDayKey;
              
              return (
                <div key={day.key} className="flex gap-0.5 mb-0.5">
                  {/* Day Label */}
                  <div className={`w-16 flex-shrink-0 flex items-center text-sm font-medium ${
                    isCurrentDay ? 'text-emerald-400' : (isLight ? 'text-gray-600' : 'text-slate-300')
                  }`}>
                    {day.shortLabel}
                    {isCurrentDay && (
                      <div className="w-1.5 h-1.5 bg-emerald-400 rounded-full ml-1 animate-pulse" />
                    )}
                  </div>
                  
                  {/* Slot Cells - Width proportional to duration */}
                  {slotConfig.map((slot, slotIdx) => {
                    const scheduledPlaylist = getPlaylistForSlot(day.key, slotIdx);
                    const isHovered = hoveredCell?.day === day.key && hoveredCell?.slotIndex === slotIdx && draggedPlaylist && !scheduledPlaylist;
                    const color = scheduledPlaylist ? getPlaylistColor(scheduledPlaylist.id) : null;
                    const isCurrentSlot = isSlotCurrentlyPlaying(day.key, slotIdx);
                    const slotDuration = slot.end_hour - slot.start_hour;
                    
                    return (
                      <div
                        key={`${day.key}-${slotIdx}`}
                        onDragOver={(e) => handleDragOver(e, day.key, slotIdx)}
                        onDragLeave={handleDragLeave}
                        onDrop={(e) => handleDrop(e, day.key, slotIdx)}
                        className={`rounded border transition-all group ${
                          isCurrentSlot && scheduledPlaylist ? 'ring-2 ring-emerald-400' : ''
                        }`}
                        style={{
                          flex: slotDuration,
                          minWidth: `${slotDuration * 50}px`,
                          height: '56px',
                          backgroundColor: scheduledPlaylist 
                            ? color.bg
                            : isHovered
                            ? isLight ? 'rgba(233, 69, 96, 0.1)' : 'rgba(124, 58, 237, 0.15)'
                            : isLight ? '#f9f9f9' : 'rgba(15, 23, 42, 0.5)',
                          borderColor: isHovered
                            ? isLight ? '#e94560' : '#8b5cf6'
                            : scheduledPlaylist
                            ? color.border
                            : isLight ? '#ebe7e0' : 'rgb(51, 65, 85)'
                        }}
                        data-testid={`schedule-cell-${day.key}-${slot.start_hour}`}
                      >
                        {scheduledPlaylist ? (
                          <div className="p-1 h-full flex items-center justify-center overflow-hidden relative group">
                            {/* Delete button - appears on hover */}
                            {(!isCurrentSlot || isSuperAdmin) && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (isCurrentSlot && isSuperAdmin) {
                                    if (!window.confirm('This slot is currently playing. Removing it will stop playback. Continue?')) {
                                      return;
                                    }
                                  }
                                  const playlistSlotIndex = scheduledPlaylist.time_slots.findIndex(ts => {
                                    const tsDay = ts.day_of_week || 'monday';
                                    const tsStartHour = parseInt(ts.start_time.split(':')[0]);
                                    const tsEndHour = parseInt(ts.end_time.split(':')[0]);
                                    return tsDay === day.key && 
                                           tsStartHour < slot.end_hour && 
                                           tsEndHour > slot.start_hour;
                                  });
                                  if (playlistSlotIndex !== -1) {
                                    handleDeleteSchedule(scheduledPlaylist, playlistSlotIndex);
                                  }
                                }}
                                className={`absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity p-0.5 ${isCurrentSlot && isSuperAdmin ? 'text-amber-400' : ''}`}
                                title={isCurrentSlot && isSuperAdmin ? 'Remove (Super Admin override)' : 'Remove schedule'}
                              >
                                <Trash2 className={`w-2.5 h-2.5 ${isCurrentSlot && isSuperAdmin ? 'text-amber-400' : 'text-red-400'}`} />
                              </button>
                            )}
                            {/* Playing indicator */}
                            {isCurrentSlot && (
                              <Lock className="w-3 h-3 text-emerald-400 absolute top-1 left-1" />
                            )}
                            {/* Centered playlist name with word wrap */}
                            <p 
                              className="text-xs font-medium text-center px-1 leading-tight break-words overflow-hidden"
                              style={{ 
                                display: '-webkit-box',
                                WebkitLineClamp: 3,
                                WebkitBoxOrient: 'vertical',
                                wordBreak: 'break-word',
                                color: scheduledPlaylist.font_color === 'black' ? '#000000' 
                                     : scheduledPlaylist.font_color === 'gray' ? '#6b7280' 
                                     : '#ffffff'
                              }}
                              title={scheduledPlaylist.name}
                            >
                              {scheduledPlaylist.name}
                            </p>
                          </div>
                        ) : (
                          <div className="h-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                            <p className="text-[10px] text-slate-500">
                              Drop{slotDuration > 1 ? ` (${slotDuration}h)` : ''}
                            </p>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>

        {/* Slot Manager Dialog */}
        <Dialog open={slotManagerOpen} onOpenChange={setSlotManagerOpen}>
          <DialogContent className={`max-w-4xl w-[90vw] p-0 overflow-hidden ${isLight ? 'bg-white border-[#ebe7e0] text-gray-800' : 'bg-slate-900 border-slate-800 text-white'}`}>
            <div className={`p-6 pb-4 ${isLight ? 'bg-[#faf8f5]' : 'bg-slate-800/50'}`}>
              <DialogHeader>
                <DialogTitle className={`flex items-center gap-2 text-xl ${isLight ? 'text-gray-800' : ''}`}>
                  <Settings className={`w-5 h-5 ${isLight ? 'text-[#e94560]' : 'text-violet-400'}`} />
                  Manage Time Slots
                </DialogTitle>
                <DialogDescription className={`mt-2 ${isLight ? 'text-gray-500' : 'text-slate-400'}`}>
                  Merge consecutive slots to create longer time blocks. Changes apply to all days.
                </DialogDescription>
              </DialogHeader>
            </div>

            <div className="p-6 pt-4 space-y-5">
              {/* Current Slot Configuration */}
              <div className="space-y-3">
                <Label className={`text-sm font-medium ${isLight ? 'text-gray-600' : 'text-slate-300'}`}>Current Time Slots</Label>
                <div className="space-y-2 max-h-72 overflow-y-auto pr-2">
                  {tempSlotConfig.map((slot, idx) => {
                    const duration = slot.end_hour - slot.start_hour;
                    const canMerge = idx < tempSlotConfig.length - 1;
                    const canSplit = duration >= 2;
                    
                    return (
                      <div 
                        key={idx}
                        className={`flex items-center justify-between p-4 rounded-xl border transition-colors ${
                          isLight 
                            ? 'bg-[#faf8f5] border-[#ebe7e0] hover:border-[#e94560]/30'
                            : 'bg-slate-800/50 border-slate-700/50 hover:border-slate-600'
                        }`}
                      >
                        <div className="flex items-center gap-4">
                          <div className={`px-4 py-1.5 rounded-full text-sm font-medium whitespace-nowrap ${
                            duration > 1 
                              ? isLight 
                                ? 'bg-[#e94560]/10 border border-[#e94560]/30 text-[#e94560]' 
                                : 'bg-violet-600/20 border border-violet-500/30 text-violet-300'
                              : isLight
                                ? 'bg-gray-100 text-gray-600'
                                : 'bg-slate-700 text-slate-300'
                          }`}>
                            {formatHour(slot.start_hour)} - {formatHour(slot.end_hour)}
                          </div>
                          <span className={`text-sm whitespace-nowrap ${isLight ? 'text-gray-400' : 'text-slate-500'}`}>
                            {duration} hour{duration > 1 ? 's' : ''}
                          </span>
                        </div>
                        
                        <div className="flex items-center gap-2 flex-shrink-0 ml-4">
                          {canSplit && (
                            <Button
                              onClick={() => splitSlot(idx)}
                              variant="outline"
                              size="sm"
                              className={`h-9 px-4 whitespace-nowrap ${
                                isLight 
                                  ? 'border-[#ebe7e0] text-gray-600 hover:bg-gray-50 hover:border-gray-300'
                                  : 'border-slate-600 text-slate-300 hover:bg-slate-700 hover:border-slate-500'
                              }`}
                              title="Split this slot"
                            >
                              <Split className="w-3.5 h-3.5 mr-2" />
                              Split
                            </Button>
                          )}
                          {canMerge && (
                            <Button
                              onClick={() => mergeSlotWithNext(idx)}
                              size="sm"
                              className={`h-9 px-4 whitespace-nowrap ${
                                isLight
                                  ? 'bg-[#e94560] hover:bg-[#d63d56] text-white'
                                  : 'bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-500 hover:to-purple-500 border border-violet-500/50 text-white shadow-lg shadow-violet-500/20'
                              }`}
                              title={`Merge with ${formatHour(tempSlotConfig[idx + 1]?.start_hour)}`}
                            >
                              <Merge className="w-3.5 h-3.5 mr-2" />
                              Merge ↓
                            </Button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Visual Preview */}
              <div className={`border rounded-xl p-4 ${isLight ? 'border-[#ebe7e0] bg-[#faf8f5]' : 'border-slate-700/50 bg-slate-800/30'}`}>
                <Label className={`text-xs mb-3 block font-medium ${isLight ? 'text-gray-400' : 'text-slate-400'}`}>Preview</Label>
                <div className="flex gap-1.5 overflow-x-auto pb-2">
                  {tempSlotConfig.map((slot, idx) => {
                    const duration = slot.end_hour - slot.start_hour;
                    const width = duration * 48; // Preview scale
                    
                    return (
                      <div
                        key={idx}
                        className={`rounded-lg border-2 flex items-center justify-center text-xs font-medium transition-all ${
                          duration > 1 
                            ? isLight
                              ? 'bg-[#e94560]/10 border-[#e94560]/50 text-[#e94560]'
                              : 'bg-violet-600/20 border-violet-500/50 text-violet-300'
                            : isLight
                              ? 'bg-gray-100 border-gray-300 text-gray-500'
                              : 'bg-slate-800 border-slate-600 text-slate-400'
                        }`}
                        style={{ width: `${width}px`, height: '44px', flexShrink: 0 }}
                      >
                        {formatHour(slot.start_hour)}
                        {duration > 1 && <span className="ml-1">({duration}h)</span>}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Info */}
              <div className={`rounded-xl p-4 ${isLight ? 'bg-[#c4a35a]/10 border border-[#c4a35a]/30' : 'bg-amber-900/20 border border-amber-600/30'}`}>
                <p className={`text-sm ${isLight ? 'text-[#8b7a4a]' : 'text-amber-300'}`}>
                  <AlertCircle className="w-4 h-4 inline mr-2" />
                  <strong>Note:</strong> Merging slots creates larger time blocks for playlists. 
                  Existing schedules will be preserved where possible.
                </p>
              </div>
            </div>

            <div className={`flex justify-between p-6 pt-4 border-t ${isLight ? 'border-[#ebe7e0] bg-[#faf8f5]' : 'border-slate-800 bg-slate-800/50'}`}>
              <Button
                onClick={resetSlotConfig}
                variant="outline"
                className={`h-10 ${isLight ? 'border-[#ebe7e0] text-gray-600 hover:bg-gray-50' : 'border-slate-600 text-slate-300 hover:bg-slate-700 hover:border-slate-500'}`}
              >
                <RotateCcw className="w-4 h-4 mr-2" />
                Reset to Default
              </Button>
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => setSlotManagerOpen(false)}
                  className={`h-10 px-5 ${isLight ? 'border-[#ebe7e0] text-gray-600 hover:bg-gray-50' : 'border-slate-600 text-slate-300 hover:bg-slate-700 hover:border-slate-500'}`}
                >
                  Cancel
                </Button>
                <Button
                  onClick={saveSlotConfig}
                  className={`h-10 px-5 ${isLight ? 'bg-[#e94560] hover:bg-[#d63d56] text-white' : 'bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-500 hover:to-purple-500 border border-violet-500/50 text-white shadow-lg shadow-violet-500/20'}`}
                >
                  <Save className="w-4 h-4 mr-2" />
                  Save Changes
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Today's Schedule Details - Collapsible */}
        <Collapsible defaultOpen={true} className={`rounded-xl ${isLight ? 'bg-white border border-[#ebe7e0]' : 'bg-slate-900/40 border border-slate-800'}`}>
          <CollapsibleTrigger className={`w-full p-6 flex items-center justify-between transition-colors rounded-xl ${isLight ? 'hover:bg-gray-50' : 'hover:bg-slate-800/30'}`}>
            <h3 className={`text-lg font-semibold flex items-center gap-2 ${isLight ? 'text-gray-800' : 'text-white'}`}>
              <Clock className={`w-5 h-5 ${isLight ? 'text-[#e94560]' : 'text-violet-400'}`} />
              Today&apos;s Schedule ({DAYS_OF_WEEK.find(d => d.key === currentDayKey)?.label})
            </h3>
            <div className={`flex items-center gap-2 ${isLight ? 'text-gray-400' : 'text-slate-400'}`}>
              <span className="text-sm">Click to expand/collapse</span>
              <ChevronDown className="w-5 h-5 transition-transform duration-200" />
            </div>
          </CollapsibleTrigger>
          <CollapsibleContent className="px-6 pb-6">
            <div className="space-y-3">
              {slotConfig.map((slot, slotIdx) => {
                const slotDuration = slot.end_hour - slot.start_hour;
                const timeLabel = `${formatHour(slot.start_hour)} - ${formatHour(slot.end_hour)}`;
                
                const scheduledPlaylist = getPlaylistForSlot(currentDayKey, slotIdx);
                const playlistSlot = scheduledPlaylist?.time_slots?.find(s => {
                  const slotDay = s.day_of_week || 'monday';
                  const tsStartHour = parseInt(s.start_time.split(':')[0]);
                  const tsEndHour = parseInt(s.end_time.split(':')[0]);
                  return slotDay === currentDayKey && tsStartHour < slot.end_hour && tsEndHour > slot.start_hour;
                });
                const itemTimes = scheduledPlaylist && playlistSlot ? calculateItemTimes(scheduledPlaylist, playlistSlot) : [];
                
                const isActive = isSlotCurrentlyPlaying(currentDayKey, slotIdx);
                const color = scheduledPlaylist ? getPlaylistColor(scheduledPlaylist.id) : null;
                
                return (
                  <div 
                    key={slotIdx} 
                    className={`border rounded-lg overflow-hidden ${
                      isActive 
                        ? 'border-emerald-500/50' 
                        : scheduledPlaylist 
                        ? (isLight ? 'border-[#ebe7e0]' : 'border-slate-700')
                        : (isLight ? 'border-[#ebe7e0]/50' : 'border-slate-800/50')
                    }`}
                  >
                    <div 
                      className={`p-3 flex items-center justify-between ${
                        isActive 
                          ? 'bg-emerald-500/10' 
                          : scheduledPlaylist 
                          ? (isLight ? 'bg-[#faf8f5]' : 'bg-slate-800/50')
                          : (isLight ? 'bg-gray-50' : 'bg-slate-900/30')
                      }`}
                      style={scheduledPlaylist ? { borderLeft: `3px solid ${color.border}` } : {}}
                    >
                      <div className="flex items-center gap-3">
                        <span className={`font-mono text-sm w-36 ${isLight ? 'text-gray-500' : 'text-slate-400'}`}>
                          {timeLabel}
                          {slotDuration > 1 && (
                            <span className={`ml-1 ${isLight ? 'text-[#e94560]' : 'text-violet-400'}`}>({slotDuration}h)</span>
                          )}
                        </span>
                        {scheduledPlaylist ? (
                          <div className="flex items-center gap-2">
                            {isActive && <div className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse" />}
                            <span className={`font-medium ${isLight ? 'text-gray-800' : 'text-white'}`}>{scheduledPlaylist.name}</span>
                            {isActive && (
                              <span className="px-2 py-0.5 bg-emerald-600/20 border border-emerald-600/30 rounded text-xs text-emerald-600 flex items-center gap-1">
                                <Lock className="w-3 h-3" />
                                PLAYING
                              </span>
                            )}
                          </div>
                        ) : (
                          <span className={`italic ${isLight ? 'text-gray-400' : 'text-slate-600'}`}>No playlist scheduled</span>
                        )}
                      </div>
                      {scheduledPlaylist && (
                        <span className={`text-xs ${isLight ? 'text-gray-400' : 'text-slate-500'}`}>{itemTimes.length} items (with loops)</span>
                      )}
                    </div>
                    
                    {scheduledPlaylist && itemTimes.length > 0 && (
                      <div className={`p-3 max-h-48 overflow-y-auto ${isActive ? 'bg-emerald-500/5' : (isLight ? 'bg-white' : 'bg-slate-800/20')}`}>
                        <table className="w-full text-sm">
                          <thead>
                            <tr className={`text-xs ${isLight ? 'text-gray-400' : 'text-slate-500'}`}>
                              <th className="text-left pb-2 w-8">#</th>
                              <th className="text-left pb-2">Item</th>
                              <th className="text-left pb-2">Genre</th>
                              <th className="text-left pb-2">Start</th>
                              <th className="text-left pb-2">End</th>
                              <th className="text-left pb-2">Duration</th>
                              <th className="text-left pb-2">Loop</th>
                            </tr>
                          </thead>
                          <tbody>
                            {itemTimes.map((item, idx) => (
                              <tr key={idx} className={`border-t ${isLight ? 'text-gray-700 border-[#ebe7e0]/50' : 'text-slate-300 border-slate-800/50'}`}>
                                <td className={`py-1.5 ${isLight ? 'text-gray-400' : 'text-slate-600'}`}>{idx + 1}</td>
                                <td className="py-1.5 flex items-center gap-2">
                                  {item.type === 'song' ? (
                                    <Music className={`w-3 h-3 ${isLight ? 'text-[#e94560]' : 'text-violet-400'}`} />
                                  ) : (
                                    <Mic className={`w-3 h-3 ${isLight ? 'text-[#c4a35a]' : 'text-amber-400'}`} />
                                  )}
                                  <span className="truncate max-w-[180px]">{item.name}</span>
                                </td>
                                <td className="py-1.5">
                                  {item.type === 'song' && item.genre ? (
                                    <span className={`px-1.5 py-0.5 rounded text-xs ${isLight ? 'bg-gray-100 text-gray-600' : 'bg-slate-700 text-slate-300'}`}>
                                      {item.genre}
                                    </span>
                                  ) : (
                                    <span className={`text-xs ${isLight ? 'text-gray-400' : 'text-slate-600'}`}>-</span>
                                  )}
                                </td>
                                <td className="py-1.5 font-mono text-xs">{item.startTime}</td>
                                <td className="py-1.5 font-mono text-xs">{item.endTime}</td>
                                <td className={`py-1.5 ${isLight ? 'text-gray-400' : 'text-slate-500'}`}>{item.duration}</td>
                                <td className="py-1.5">
                                  <span className={`px-1.5 py-0.5 rounded text-xs ${
                                    item.loopNumber > 1 
                                      ? (isLight ? 'bg-[#e94560]/10 text-[#e94560]' : 'bg-violet-600/20 text-violet-400')
                                      : (isLight ? 'bg-gray-100 text-gray-500' : 'bg-slate-800 text-slate-500')
                                  }`}>
                                    L{item.loopNumber}
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </CollapsibleContent>
        </Collapsible>

        {/* Instructions */}
        <div className={`border rounded-lg p-4 text-sm ${isLight ? 'bg-[#faf8f5] border-[#ebe7e0] text-gray-600' : 'bg-slate-800/30 border-slate-700 text-slate-300'}`}>
          <p className="font-medium mb-2">How it works:</p>
          <ol className={`list-decimal list-inside space-y-1 ${isLight ? 'text-gray-500' : 'text-slate-400'}`}>
            <li>Create playlists with songs and announcements</li>
            <li>Use <strong className={isLight ? 'text-[#e94560]' : 'text-violet-400'}>Manage Time Slots</strong> to merge hours into longer blocks</li>
            <li>Drag any playlist to a <strong className={isLight ? 'text-[#e94560]' : 'text-violet-400'}>day and time slot</strong></li>
            <li><strong className={isLight ? 'text-[#e94560]' : 'text-violet-400'}>Times auto-calculate</strong> for each song based on duration</li>
            <li>Playlists <strong className={isLight ? 'text-[#e94560]' : 'text-violet-400'}>loop automatically</strong> to fill the time slot</li>
            <li><strong className="text-emerald-500">Cannot edit/delete</strong> playlists while they are playing</li>
            <li>Dashboard shows real-time playback for today&apos;s schedule</li>
          </ol>
        </div>
      </div>

      {/* Edit Playlist Dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className={`max-w-5xl w-[90vw] max-h-[85vh] overflow-hidden ${isLight ? 'bg-white border-[#ebe7e0] text-gray-800' : 'bg-slate-900 border-slate-800 text-white'}`}>
          <DialogHeader>
            <DialogTitle className={isLight ? 'text-gray-800' : ''}>Edit Playlist</DialogTitle>
          </DialogHeader>
          {editingPlaylist && (
            <PlaylistForm
              playlist={editingPlaylist}
              setPlaylist={setEditingPlaylist}
              songs={songs}
              announcements={announcements}
              genres={genres}
              onAddItem={handleAddItem}
              onRemoveItem={handleRemoveItem}
              onMoveUp={handleMoveItemUp}
              onMoveDown={handleMoveItemDown}
              onSave={handleUpdatePlaylist}
              onCancel={() => setEditOpen(false)}
              calculateDuration={calculatePlaylistDuration}
              formatDuration={formatDuration}
              isEdit
              isSuperAdmin={isSuperAdmin}
              businesses={businesses}
              isLight={isLight}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Genre configuration for tiles
const GENRE_CONFIG = {
  'Pop': { color: 'from-pink-500 to-rose-600', image: 'https://images.unsplash.com/photo-1514525253161-7a46d19cd819?w=200&h=120&fit=crop' },
  'Rock': { color: 'from-red-500 to-orange-600', image: 'https://images.unsplash.com/photo-1498038432885-c6f3f1b912ee?w=200&h=120&fit=crop' },
  'Jazz': { color: 'from-amber-500 to-yellow-600', image: 'https://images.unsplash.com/photo-1511192336575-5a79af67a629?w=200&h=120&fit=crop' },
  'Classical': { color: 'from-violet-500 to-purple-600', image: 'https://images.unsplash.com/photo-1507838153414-b4b713384a76?w=200&h=120&fit=crop' },
  'Electronic': { color: 'from-cyan-500 to-blue-600', image: 'https://images.unsplash.com/photo-1571266028243-e4733b0f0bb0?w=200&h=120&fit=crop' },
  'Hip Hop': { color: 'from-emerald-500 to-teal-600', image: 'https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=200&h=120&fit=crop' },
  'Country': { color: 'from-orange-500 to-amber-600', image: 'https://images.unsplash.com/photo-1510915361894-db8b60106cb1?w=200&h=120&fit=crop' },
  'R&B': { color: 'from-purple-500 to-pink-600', image: 'https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=200&h=120&fit=crop' },
  'Ambient': { color: 'from-slate-500 to-slate-600', image: 'https://images.unsplash.com/photo-1518972559570-7cc1309f3229?w=200&h=120&fit=crop' },
  'Other': { color: 'from-gray-500 to-gray-600', image: 'https://images.unsplash.com/photo-1511379938547-c1f69419868d?w=200&h=120&fit=crop' }
};

const ANNOUNCEMENT_TYPES = ['Store Opening', 'Store Closing', 'Promotion', 'Safety', 'General', 'Holiday', 'Event', 'Other'];
const ANNOUNCEMENT_CONFIG = {
  'Store Opening': { color: 'from-emerald-500 to-green-600', icon: '🌅' },
  'Store Closing': { color: 'from-indigo-500 to-blue-600', icon: '🌙' },
  'Promotion': { color: 'from-amber-500 to-orange-600', icon: '🎉' },
  'Safety': { color: 'from-red-500 to-rose-600', icon: '⚠️' },
  'General': { color: 'from-slate-500 to-gray-600', icon: '📢' },
  'Holiday': { color: 'from-pink-500 to-rose-600', icon: '🎄' },
  'Event': { color: 'from-violet-500 to-purple-600', icon: '🎪' },
  'Other': { color: 'from-gray-500 to-slate-600', icon: '📋' }
};

// Reusable Playlist Form Component with Genre-based Selection
// Sortable Item Component for drag and drop
const SortablePlaylistItem = ({ item, index, onRemove, formatDuration, isLight }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.itemId });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1000 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-center gap-1.5 p-1.5 rounded transition-colors ${
        isDragging 
          ? (isLight ? 'bg-[#e94560]/10 border-[#e94560] shadow-lg' : 'bg-violet-500/20 border-violet-500 shadow-lg')
          : (isLight ? 'bg-white border border-[#ebe7e0] hover:border-[#e94560]/30' : 'bg-slate-900/50 hover:bg-slate-900/70')
      } ${isDragging ? 'border' : ''}`}
    >
      {/* Drag Handle */}
      <button
        {...attributes}
        {...listeners}
        className={`cursor-grab active:cursor-grabbing p-0.5 rounded hover:bg-gray-100 dark:hover:bg-slate-700 ${isLight ? 'text-gray-400 hover:text-gray-600' : 'text-slate-500 hover:text-slate-300'}`}
        title="Drag to reorder"
      >
        <GripVertical className="w-3.5 h-3.5" />
      </button>
      
      <span className={`text-[10px] font-mono w-4 text-center ${isLight ? 'text-gray-400' : 'text-slate-500'}`}>{index + 1}</span>
      {item.type === 'song' ? (
        <Music className={`w-3 h-3 flex-shrink-0 ${isLight ? 'text-[#e94560]' : 'text-violet-400'}`} />
      ) : (
        <Mic className={`w-3 h-3 flex-shrink-0 ${isLight ? 'text-[#c4a35a]' : 'text-amber-400'}`} />
      )}
      <div className="flex-1 min-w-0">
        <p className={`text-xs truncate font-medium leading-tight ${isLight ? 'text-gray-700' : 'text-slate-200'}`}>
          {item.name}
        </p>
        <p className={`text-[10px] truncate leading-tight ${isLight ? 'text-gray-400' : 'text-slate-500'}`}>
          {item.type === 'song' ? item.artist : 'Announcement'} • {formatDuration(item.duration || (item.type === 'song' ? 180 : 30))}
        </p>
      </div>
      <Button
        onClick={() => onRemove(item.itemId)}
        variant="ghost"
        className={`h-5 w-5 p-0 ${isLight ? 'hover:text-red-500' : 'hover:text-red-400'}`}
        title="Remove"
      >
        <X className={`w-2.5 h-2.5 ${isLight ? 'text-gray-400' : 'text-slate-400'}`} />
      </Button>
    </div>
  );
};

const PlaylistForm = ({ playlist, setPlaylist, songs, announcements, genres, onAddItem, onRemoveItem, onSave, onCancel, onMoveUp, onMoveDown, calculateDuration, formatDuration, isEdit = false, isSuperAdmin = false, businesses = [], isLight = false }) => {
  const [expandedGenres, setExpandedGenres] = useState({});
  const [expandedAnnouncementTypes, setExpandedAnnouncementTypes] = useState({});
  const totalDuration = calculateDuration(playlist.items);

  // Get genre names from the genres prop
  const genreNames = genres.map(g => typeof g === 'string' ? g : g.name);
  
  // Group songs by genre
  const songsByGenre = genreNames.reduce((acc, genreName) => {
    acc[genreName] = songs.filter(song => song.genre === genreName);
    return acc;
  }, {});

  // Group announcements by type
  const announcementsByType = ANNOUNCEMENT_TYPES.reduce((acc, type) => {
    acc[type] = announcements.filter(a => (a.genre || 'General') === type);
    return acc;
  }, {});

  const toggleGenre = (genreName) => {
    setExpandedGenres(prev => ({ ...prev, [genreName]: !prev[genreName] }));
  };

  const toggleAnnouncementType = (type) => {
    setExpandedAnnouncementTypes(prev => ({ ...prev, [type]: !prev[type] }));
  };
  
  const handleBusinessToggle = (businessId) => {
    const currentAssigned = playlist.assigned_businesses || [];
    if (currentAssigned.includes(businessId)) {
      setPlaylist({ 
        ...playlist, 
        assigned_businesses: currentAssigned.filter(id => id !== businessId) 
      });
    } else {
      setPlaylist({ 
        ...playlist, 
        assigned_businesses: [...currentAssigned, businessId] 
      });
    }
  };
  
  // Helper to get genre config
  const getGenreConfig = (genreName) => {
    const genre = genres.find(g => (typeof g === 'string' ? g : g.name) === genreName);
    if (genre && typeof genre === 'object') {
      return {
        image: genre.image,
        color: genre.color,
      };
    }
    return GENRE_CONFIG[genreName] || GENRE_CONFIG['Other'];
  };

  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Handle drag end - reorder items
  const handleDragEnd = (event) => {
    const { active, over } = event;
    
    if (active.id !== over?.id) {
      const oldIndex = playlist.items.findIndex(item => item.itemId === active.id);
      const newIndex = playlist.items.findIndex(item => item.itemId === over.id);
      
      const newItems = arrayMove(playlist.items, oldIndex, newIndex);
      setPlaylist({ ...playlist, items: newItems });
    }
  };
  
  return (
    <div className="flex gap-6 h-[calc(85vh-120px)]">
      {/* Left Column - Form Fields */}
      <div className="flex-1 basis-1/2 space-y-4 overflow-y-auto pr-4">
        <div>
          <Label className={isLight ? 'text-gray-600' : ''}>Playlist Name</Label>
          <Input
            value={playlist.name}
            onChange={(e) => setPlaylist({ ...playlist, name: e.target.value })}
            placeholder="e.g., Morning Vibes"
            className={`mt-1.5 ${isLight ? 'bg-[#faf8f5] border-[#ebe7e0] text-gray-800' : 'bg-slate-950 border-slate-800 text-white'}`}
          />
        </div>

        {/* Color Picker */}
        <div>
          <Label className={`flex items-center gap-2 mb-2 ${isLight ? 'text-gray-600' : ''}`}>
            <svg className={`w-4 h-4 ${isLight ? 'text-[#e94560]' : 'text-violet-400'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
            </svg>
            Playlist Color
          </Label>
          <div className={`border rounded-lg p-3 ${isLight ? 'border-[#ebe7e0] bg-[#faf8f5]' : 'border-slate-800 bg-slate-950/50'}`}>
            <div className="grid grid-cols-10 gap-1.5">
              {PLAYLIST_COLORS.map((color) => (
                <button
                  key={color.hex}
                  type="button"
                  onClick={() => setPlaylist({ ...playlist, color: color.hex })}
                  className={`w-5 h-5 rounded-full transition-all hover:scale-110 border ${
                    playlist.color === color.hex 
                      ? 'ring-2 ring-offset-1 ' + (isLight ? 'ring-gray-400 ring-offset-white' : 'ring-white ring-offset-slate-900')
                      : isLight ? 'border-gray-200' : 'border-slate-700'
                  }`}
                  style={{ backgroundColor: color.hex }}
                  title={color.name}
                />
              ))}
            </div>
            {playlist.color && (
              <div className="flex items-center justify-between mt-3 pt-2 border-t" style={{ borderColor: isLight ? '#ebe7e0' : '#1e293b' }}>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded" style={{ backgroundColor: playlist.color }} />
                  <span className={`text-sm ${isLight ? 'text-gray-600' : 'text-slate-300'}`}>
                    {PLAYLIST_COLORS.find(c => c.hex === playlist.color)?.name || 'Custom'}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => setPlaylist({ ...playlist, color: null })}
                  className={`text-xs ${isLight ? 'text-gray-400 hover:text-gray-600' : 'text-slate-500 hover:text-slate-300'}`}
                >
                  Clear
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Business Assignment - Only for Super Admin */}
        {isSuperAdmin && businesses.length > 0 && (
          <div>
            <Label className={`flex items-center gap-2 mb-2 ${isLight ? 'text-gray-600' : ''}`}>
              <svg className={`w-4 h-4 ${isLight ? 'text-[#e94560]' : 'text-violet-400'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
              Assign to Businesses (optional)
            </Label>
            <div className={`border rounded-lg p-3 ${isLight ? 'border-[#ebe7e0] bg-[#faf8f5]' : 'border-slate-800 bg-slate-950/50'}`}>
              <p className={`text-xs mb-2 ${isLight ? 'text-gray-400' : 'text-slate-500'}`}>Select businesses to share this playlist with. They can schedule it for playback.</p>
              <div className="space-y-2 max-h-32 overflow-y-auto">
                {businesses.map((business) => (
                  <label 
                    key={business.id}
                    className={`flex items-center gap-2 p-2 rounded-lg cursor-pointer transition-colors ${isLight ? 'hover:bg-gray-100' : 'hover:bg-slate-800/50'}`}
                  >
                    <input
                      type="checkbox"
                      checked={(playlist.assigned_businesses || []).includes(business.id)}
                      onChange={() => handleBusinessToggle(business.id)}
                      className={`w-4 h-4 rounded ${isLight ? 'border-gray-300 bg-white text-[#e94560] focus:ring-[#e94560]' : 'border-slate-600 bg-slate-800 text-violet-500 focus:ring-violet-500 focus:ring-offset-slate-900'}`}
                    />
                    <span className={`text-sm ${isLight ? 'text-gray-700' : 'text-slate-300'}`}>{business.name}</span>
                  </label>
                ))}
              </div>
              {(playlist.assigned_businesses || []).length > 0 && (
                <p className={`text-xs mt-2 ${isLight ? 'text-[#e94560]' : 'text-violet-400'}`}>
                  Assigned to {(playlist.assigned_businesses || []).length} business(es)
                </p>
              )}
            </div>
          </div>
        )}

        {/* Genre-based Song Selection */}
        <div>
          <Label className={`flex items-center gap-2 mb-2 ${isLight ? 'text-gray-600' : ''}`}>
            <Music className={`w-4 h-4 ${isLight ? 'text-[#e94560]' : 'text-rose-400'}`} />
            Add Songs by Genre
          </Label>
          <div className={`border rounded-lg overflow-hidden max-h-56 overflow-y-auto ${isLight ? 'border-[#ebe7e0]' : 'border-slate-800'}`}>
            {songs.length === 0 ? (
              <p className={`text-sm p-4 text-center ${isLight ? 'text-gray-400' : 'text-slate-500'}`}>No songs available</p>
            ) : (
              <div className={`divide-y ${isLight ? 'divide-[#ebe7e0]' : 'divide-slate-800'}`}>
                {genreNames.map((genreName) => {
                  const genreSongs = songsByGenre[genreName] || [];
                  const config = getGenreConfig(genreName);
                  const isExpanded = expandedGenres[genreName];
                  const songCount = genreSongs.length;
                  
                  if (songCount === 0) return null;
                
                return (
                  <div key={genreName}>
                    {/* Genre Header - Clickable Tile */}
                    <button
                      type="button"
                      onClick={() => toggleGenre(genreName)}
                      className={`w-full flex items-center gap-3 p-2 transition-colors ${isLight ? 'hover:bg-gray-50' : 'hover:bg-slate-800/50'}`}
                    >
                      <div 
                        className={`w-12 h-8 rounded overflow-hidden bg-gradient-to-r ${config.color} flex items-center justify-center flex-shrink-0`}
                      >
                        <Music className="w-4 h-4 text-white" />
                      </div>
                      <div className="flex-1 text-left">
                        <span className={`text-sm font-medium ${isLight ? 'text-gray-800' : 'text-white'}`}>{genreName}</span>
                        <span className={`text-xs ml-2 ${isLight ? 'text-gray-400' : 'text-slate-500'}`}>({songCount} songs)</span>
                      </div>
                      {isExpanded ? (
                        <ChevronDown className={`w-4 h-4 ${isLight ? 'text-gray-400' : 'text-slate-400'}`} />
                      ) : (
                        <ChevronRight className={`w-4 h-4 ${isLight ? 'text-gray-400' : 'text-slate-400'}`} />
                      )}
                    </button>

                    {/* Songs List - Expandable */}
                    {isExpanded && (
                      <div className={`border-t ${isLight ? 'bg-gray-50/50 border-[#ebe7e0]' : 'bg-slate-950/50 border-slate-800'}`}>
                        {genreSongs.map((song) => (
                          <div key={song.id} className={`flex items-center justify-between py-2 px-4 ${isLight ? 'hover:bg-gray-100' : 'hover:bg-slate-800/30'}`}>
                            <div className="flex-1 min-w-0">
                              <p className={`text-sm truncate ${isLight ? 'text-gray-700' : 'text-slate-300'}`}>{song.name}</p>
                              <p className={`text-xs truncate ${isLight ? 'text-gray-400' : 'text-slate-500'}`}>{song.artist} • {formatDuration(song.duration || 180)}</p>
                            </div>
                            <Button
                              type="button"
                              onClick={() => onAddItem(song, 'song')}
                              className={`h-7 px-3 text-xs ml-2 flex-shrink-0 ${isLight ? 'bg-[#e94560] hover:bg-[#d63d56]' : 'bg-rose-600 hover:bg-rose-500'}`}
                            >
                              Add
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

        {/* Announcements Section */}
        <div>
          <Label className={`flex items-center gap-2 mb-2 ${isLight ? 'text-gray-600' : ''}`}>
            <Mic className={`w-4 h-4 ${isLight ? 'text-[#c4a35a]' : 'text-amber-400'}`} />
            Add Announcements by Type
          </Label>
          <div className={`border rounded-lg overflow-hidden max-h-40 overflow-y-auto ${isLight ? 'border-[#ebe7e0]' : 'border-slate-800'}`}>
            {announcements.length === 0 ? (
              <p className={`text-sm p-4 text-center ${isLight ? 'text-gray-400' : 'text-slate-500'}`}>No announcements available</p>
            ) : (
              <div className={`divide-y ${isLight ? 'divide-[#ebe7e0]' : 'divide-slate-800'}`}>
                {ANNOUNCEMENT_TYPES.map((type) => {
                  const typeAnnouncements = announcementsByType[type];
                  const config = ANNOUNCEMENT_CONFIG[type] || ANNOUNCEMENT_CONFIG['Other'];
                  const isExpanded = expandedAnnouncementTypes[type];
                  const count = typeAnnouncements.length;
                  
                  if (count === 0) return null;
                  
                  return (
                    <div key={type}>
                      {/* Type Header */}
                      <button
                        type="button"
                        onClick={() => toggleAnnouncementType(type)}
                        className={`w-full flex items-center gap-3 p-2 transition-colors ${isLight ? 'hover:bg-gray-50' : 'hover:bg-slate-800/50'}`}
                      >
                        <div 
                          className={`w-12 h-8 rounded bg-gradient-to-r ${config.color} flex items-center justify-center flex-shrink-0`}
                        >
                          <span className="text-sm">{config.icon}</span>
                        </div>
                        <div className="flex-1 text-left">
                          <span className={`text-sm font-medium ${isLight ? 'text-gray-800' : 'text-white'}`}>{type}</span>
                          <span className={`text-xs ml-2 ${isLight ? 'text-gray-400' : 'text-slate-500'}`}>({count})</span>
                        </div>
                        {isExpanded ? (
                          <ChevronDown className={`w-4 h-4 ${isLight ? 'text-gray-400' : 'text-slate-400'}`} />
                        ) : (
                          <ChevronRight className={`w-4 h-4 ${isLight ? 'text-gray-400' : 'text-slate-400'}`} />
                        )}
                      </button>

                      {/* Announcements List */}
                      {isExpanded && (
                        <div className={`border-t ${isLight ? 'bg-gray-50/50 border-[#ebe7e0]' : 'bg-slate-950/50 border-slate-800'}`}>
                          {typeAnnouncements.map((announcement) => (
                            <div key={announcement.id} className={`flex items-center justify-between py-2 px-4 ${isLight ? 'hover:bg-gray-100' : 'hover:bg-slate-800/30'}`}>
                              <div className="flex-1 min-w-0">
                                <p className={`text-sm truncate ${isLight ? 'text-gray-700' : 'text-slate-300'}`}>{announcement.name}</p>
                                <p className={`text-xs ${isLight ? 'text-gray-400' : 'text-slate-500'}`}>{formatDuration(announcement.duration || 30)}</p>
                              </div>
                              <Button
                                type="button"
                                onClick={() => onAddItem(announcement, 'announcement')}
                                className={`h-7 px-3 text-xs ml-2 flex-shrink-0 ${isLight ? 'bg-[#c4a35a] hover:bg-[#b3944f]' : 'bg-amber-600 hover:bg-amber-500'}`}
                              >
                                Add
                              </Button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Action Buttons at bottom of left column */}
        <div className="flex gap-2 pt-4 mt-auto">
          <Button
            variant="outline"
            onClick={onCancel}
            className={`flex-1 ${isLight ? 'border-[#ebe7e0]' : 'border-slate-700'}`}
          >
            Cancel
          </Button>
          <Button
            onClick={onSave}
            className={`flex-1 ${isLight ? 'bg-[#e94560] hover:bg-[#d63d56]' : 'bg-violet-600 hover:bg-violet-500'}`}
          >
            <Save className="w-4 h-4 mr-2" />
            {isEdit ? 'Update' : 'Create'} Playlist
          </Button>
        </div>
      </div>

      {/* Right Column - Playlist Items */}
      <div className={`flex-1 basis-1/2 border-l pl-5 flex flex-col ${isLight ? 'border-[#ebe7e0]' : 'border-slate-700'}`}>
        <div className={`rounded-lg p-3 flex-1 flex flex-col ${isLight ? 'bg-[#faf8f5] border border-[#ebe7e0]' : 'bg-slate-800/30 border border-slate-700'}`}>
          <div className="flex items-center justify-between mb-2">
            <Label className={`text-sm font-medium ${isLight ? 'text-gray-600' : 'text-slate-300'}`}>
              Playlist Items
            </Label>
            <span className={`text-xs font-medium ${isLight ? 'text-[#e94560]' : 'text-violet-400'}`}>
              {playlist.items.length} items • {formatDuration(totalDuration)}
            </span>
          </div>

          {playlist.items.length === 0 ? (
            <div className={`flex-1 flex items-center justify-center text-center p-4 ${isLight ? 'text-gray-400' : 'text-slate-500'}`}>
              <div>
                <Music className="w-10 h-10 mx-auto mb-2 opacity-30" />
                <p className="text-xs">No items added yet</p>
                <p className="text-[10px] mt-1">Add songs or announcements from the left</p>
              </div>
            </div>
          ) : (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={playlist.items.map(item => item.itemId)}
                strategy={verticalListSortingStrategy}
              >
                <div className="flex-1 overflow-y-auto space-y-1">
                  {playlist.items.map((item, index) => (
                    <SortablePlaylistItem
                      key={item.itemId}
                      item={item}
                      index={index}
                      onRemove={onRemoveItem}
                      formatDuration={formatDuration}
                      isLight={isLight}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          )}

          <p className={`text-[10px] mt-2 pt-2 border-t flex items-center gap-1 ${isLight ? 'text-gray-400 border-[#ebe7e0]' : 'text-slate-500 border-slate-700'}`}>
            <GripVertical className="w-2.5 h-2.5" />
            Drag items to reorder
          </p>
        </div>
      </div>
    </div>
  );
};

export default PlaylistScheduler;
